import { useState } from "react";
import { Bug, X, Sparkles, User, Truck, ArrowRightLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { motion, AnimatePresence } from "framer-motion";

const TEST_EMAILS = [
  "founder@akenterpriseinc.com",
  "vendor_test@test.com",
  "voter1@test.com",
  "voter2@test.com",
  "voter3@test.com",
];

/**
 * DevBadge — persistent floating badge for test/dev accounts.
 * Shows a small "DEV" pill at the top-right of every screen.
 * Tapping it opens a panel with quick account info and mode switching.
 */
const SWITCH_TARGETS = {
  "founder@akenterpriseinc.com": { to: "vendor_test@test.com", label: "Vendor", icon: "truck" },
  "vendor_test@test.com": { to: "founder@akenterpriseinc.com", label: "User", icon: "user" },
};

export default function DevBadge() {
  const { user, logout, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Only show for test accounts
  if (!user || !TEST_EMAILS.includes(user.email)) return null;

  const isVendor = user.role === "vendor";
  const isAdmin = user.is_admin;
  const switchTarget = SWITCH_TARGETS[user.email];

  const handleQuickSwitch = async () => {
    if (!switchTarget || switching) return;
    setSwitching(true);
    try {
      await logout();
      const res = await axios.post(`${API}/auth/login`, {
        email: switchTarget.to,
        password: "password123",
      });
      const token = res.data.access_token;
      localStorage.setItem("token", token);
      if (switchTarget.icon === "truck") {
        localStorage.setItem("vendorModeSelected", "true");
        localStorage.setItem("vendorModeActive", "true");
        window.location.href = "/vendor";
      } else {
        localStorage.removeItem("vendorModeSelected");
        localStorage.removeItem("vendorModeActive");
        window.location.href = "/";
      }
    } catch {
      setSwitching(false);
    }
  };

  return (
    <>
      {/* Floating badge */}
      <button
        onClick={() => setOpen(true)}
        className="fixed top-2 right-2 z-[9999] px-2 py-1 min-w-[36px] min-h-[36px] flex items-center justify-center rounded-full text-[8px] font-black tracking-wider shadow-lg transition-all hover:scale-110 active:scale-95 opacity-60 hover:opacity-100"
        style={{
          background: "linear-gradient(135deg, #f97316 0%, #ef4444 100%)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.3)",
        }}
        data-testid="dev-badge"
      >
        DEV
      </button>

      {/* Panel overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-start justify-end p-3"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, x: 20, y: -10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: 20, y: -10 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1C1C1E] rounded-2xl shadow-2xl border border-white/10 w-64 overflow-hidden"
              data-testid="dev-badge-panel"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Bug className="w-4 h-4 text-orange-400" />
                  <span className="text-xs font-bold text-white/80">Dev Panel</span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 rounded-lg hover:bg-white/10 text-white/40"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Account info */}
              <div className="px-4 py-3 space-y-2">
                <div className="flex items-center gap-2">
                  {isVendor ? (
                    <Truck className="w-4 h-4 text-amber-400" />
                  ) : (
                    <User className="w-4 h-4 text-sky-400" />
                  )}
                  <div>
                    <p className="text-xs font-bold text-white/80">
                      {user.first_name} {user.last_initial}.
                    </p>
                    <p className="text-[10px] text-white/40">{user.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div className="bg-white/5 rounded-lg px-2 py-1.5">
                    <span className="text-white/40">Role</span>
                    <p className="text-white/70 font-semibold capitalize">{user.role || "user"}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-2 py-1.5">
                    <span className="text-white/40">UID</span>
                    <p className="text-white/70 font-semibold truncate">{user.uid || "—"}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-2 py-1.5">
                    <span className="text-white/40">Onboarding</span>
                    <p className="text-white/70 font-semibold">{user.onboarding_complete ? "Done" : "Pending"}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-2 py-1.5">
                    <span className="text-white/40">Admin</span>
                    <p className="text-white/70 font-semibold">{isAdmin ? "Yes" : "No"}</p>
                  </div>
                </div>

                {user.vendor_id && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-2 py-1.5">
                    <span className="text-[10px] text-amber-400/60">Vendor ID</span>
                    <p className="text-xs text-amber-400 font-mono font-bold">{user.vendor_id}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-4 pb-3 space-y-1.5">
                {switchTarget && (
                  <button
                    onClick={handleQuickSwitch}
                    disabled={switching}
                    className="w-full py-2 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-bold hover:bg-[#F59E0B]/20 transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid="dev-quick-switch"
                  >
                    {switching ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ArrowRightLeft className="w-3.5 h-3.5" />
                    )}
                    Switch to {switchTarget.label}
                  </button>
                )}
                <button
                  onClick={() => {
                    setOpen(false);
                    window.location.href = "/admin";
                  }}
                  className="w-full py-2 rounded-lg bg-white/5 text-white/60 text-xs font-semibold hover:bg-white/10 transition flex items-center justify-center gap-1.5"
                  data-testid="dev-go-admin"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Admin Cockpit
                </button>
                <button
                  onClick={() => {
                    setOpen(false);
                    logout();
                    window.location.href = "/onboarding";
                  }}
                  className="w-full py-2 rounded-lg bg-red-500/10 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition"
                  data-testid="dev-logout"
                >
                  Switch Account
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
